import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/lib/hsUtil";

const TemplateRow = ({ t, onChanged }) => {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const rename = () => {
    const name = window.prompt("New template name", t.name);
    if (name && name.trim() !== t.name) act(() => api.put(`/hs/hazard-templates/${t.id}`, { name }), "Template renamed");
  };
  const del = () => window.confirm(`Delete template "${t.name}"? Projects that already used it keep their hazards.`)
    && act(() => api.delete(`/hs/hazard-templates/${t.id}`), "Template deleted");
  return (
    <div className="flex items-center gap-2 text-sm border-t border-neutral-800 pt-2" data-testid={`hz-template-row-${t.id}`}>
      <div className="flex-1">{t.name} <span className="text-xs opacity-60">{t.hazard_count} hazards{t.archived_count ? `, ${t.archived_count} archived` : ""}</span></div>
      <button className="btn-ghost text-xs" onClick={rename} data-testid={`hz-template-rename-${t.id}`}>Rename</button>
      <button className="btn-ghost text-xs" onClick={() => act(() => api.post(`/hs/hazard-templates/${t.id}/duplicate`), "Template duplicated")} data-testid={`hz-template-duplicate-${t.id}`}>Duplicate</button>
      <button className="btn-ghost text-xs text-red-400" onClick={del} data-testid={`hz-template-delete-${t.id}`}>Delete</button>
    </div>
  );
};

export async function applyTemplate(pid, template_id, mode) {
  const { data } = await api.post(`/hs/job-hazards/${pid}/apply-template`, { template_id, mode });
  const skip = data.skipped_archived || [];
  toast.success(`Template applied: ${data.applied} hazards ticked`
    + (skip.length ? `. Skipped ${skip.length} archived: ${skip.map(s => s.ref_id || s.hazard).join(", ")}` : ""), { duration: 8000 });
  return data;
}

export default function TemplatesBar({ pid, selectedCount, onApplied }) {
  const [list, setList] = useState([]);
  const [tid, setTid] = useState("");
  const [mode, setMode] = useState("add");
  const [manage, setManage] = useState(false);
  const load = useCallback(() => api.get("/hs/hazard-templates").then(r => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  async function save() {
    const name = window.prompt("Template name (e.g. New house build, Renovation, Commercial shed)");
    if (!name) return;
    try { await api.post("/hs/hazard-templates", { name, project_id: pid }); toast.success(`Template "${name}" saved`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  }
  async function apply() {
    if (!tid) return;
    if (mode === "replace" && !window.confirm("Replace this project's hazards with the template? Hazards not in the template will be unticked.")) return;
    try { onApplied(await applyTemplate(pid, tid, mode)); load(); } catch (e) { toast.error(errMsg(e)); }
  }
  return (
    <div className="card space-y-2" data-testid="hz-templates-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-silver text-sm">Templates</span>
        <button className="btn-ghost text-sm" disabled={!selectedCount} onClick={save} data-testid="hz-template-save">Save as template</button>
        <select className="input !w-auto" value={tid} onChange={e => setTid(e.target.value)} data-testid="hz-template-select">
          <option value="">Choose a template…</option>
          {list.map(t => <option key={t.id} value={t.id}>{t.name} ({t.hazard_count})</option>)}
        </select>
        <label className="text-sm flex items-center gap-1"><input type="radio" checked={mode === "add"} onChange={() => setMode("add")} data-testid="hz-template-mode-add"/> Add to existing</label>
        <label className="text-sm flex items-center gap-1"><input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="hz-template-mode-replace"/> Replace existing</label>
        <button className="btn-silver text-sm" disabled={!tid} onClick={apply} data-testid="hz-template-apply">Apply template</button>
        <button className="btn-ghost text-xs ml-auto" onClick={() => setManage(m => !m)} data-testid="hz-template-manage">{manage ? "Hide templates" : "Manage templates"}</button>
      </div>
      {manage && (
        <div data-testid="hz-template-list">
          {list.length === 0 && <div className="text-sm opacity-60">No templates yet. Tick hazards for a project, then Save as template.</div>}
          {list.map(t => <TemplateRow key={t.id} t={t} onChanged={load}/>)}
        </div>
      )}
    </div>
  );
}
